import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, CanActivate } from "@angular/router";
import { Store } from "@ngrx/store";
import { RegistrationConfigService } from "@obg/common.registration/sdk/registration/registration.config";
import { OpenIdConnectCacheService, OpenIdConnectState } from "@obg/common.verification";
import { AUTH_MESSAGE_CODES, CORE_ROUTE } from "@obg/ngx.core";
import { PlatformService } from "@obg/ngx.global.evo";
import { RouteAction } from "@obg/ngx.router";
import { Observable, of } from "rxjs";

import { OpenIdConnectAction } from "../../sdk/open-id-connect.action";

import { SIGNICAT_QUERY_PARAMS_ERRORS, SignicatQueryParams } from "./open-id-connect.const";

@Injectable({
	providedIn: "root",
})
export class OpenIdConnectGuard implements CanActivate {
	constructor(
		private store: Store<OpenIdConnectState>,
		private openIdConnectAction: OpenIdConnectAction,
		private openIdConnectCacheService: OpenIdConnectCacheService,
		private platformService: PlatformService,
		private registrationConfigService: RegistrationConfigService
	) {}

	canActivate(route: ActivatedRouteSnapshot): Observable<boolean> {
		if (this.registrationConfigService.getIsSignicatServiceEnabled()) {
			return this.signicatServiceCheck(route);
		}

		const token = route.queryParams?.token;
		const error = route.queryParams?.error;
		const provider = this.openIdConnectCacheService.getCurrentJourney();

		if (!token && !error) {
			if (route.queryParams?.pokerclientredirect) {
				this.store.dispatch(RouteAction.navigate(CORE_ROUTE.home, {}, { queryParamsHandling: "preserve" }));
				return of(false);
			}
			this.store.dispatch(RouteAction.navigate(CORE_ROUTE.home));

			return of(false);
		}

		if (error && error !== AUTH_MESSAGE_CODES.login.invalidCredentials) {
			this.store.dispatch(this.openIdConnectAction.setOpenIdConnectError({ error, provider }));
			const queryParams = {
				openID: true,
				oidcException: true,
			};

			this.store.dispatch(
				RouteAction.navigate(CORE_ROUTE.auth.login, undefined, { queryParams, replaceUrl: true })
			);

			return of(false);
		}

		if (token && this.platformService.isBrowser) {
			this.store.dispatch(this.openIdConnectAction.setOpenIdConnectToken({ token, provider }));
		}

		return of(true);
	}

	private signicatServiceCheck(route: ActivatedRouteSnapshot): Observable<boolean> {
		const queryParams: SignicatQueryParams = {};
		const { token, error } = route.queryParams as SignicatQueryParams;

		if (!token && !error) {
			this.store.dispatch(RouteAction.navigate(CORE_ROUTE.home));

			return of(false);
		}

		if (token) {
			queryParams.token = token;
		} else if (error) {
			error === SIGNICAT_QUERY_PARAMS_ERRORS.E_AUTHENTICATION_CHALLENGE_CUSTOMERABORTED
				? (queryParams.error = SIGNICAT_QUERY_PARAMS_ERRORS.E_AUTHENTICATION_CHALLENGE_CUSTOMERABORTED)
				: (queryParams.error = SIGNICAT_QUERY_PARAMS_ERRORS.E_AUTHENTICATION_CHALLENGE_INTERNALERROR);
		}

		this.store.dispatch(RouteAction.navigate(CORE_ROUTE.registration.register, undefined, { queryParams }));

		return of(false);
	}
}
